import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../auth/AuthAtoms';
import { unreadMessageCountAtom } from '../atoms/messageAtoms';
import {
    fetchInbox,
    fetchSent,
    markMessageAsRead,
    sendMessage,
    broadcastMessage,
    fetchUnreadCount,
} from '../api/messages';
import { fetchUsers } from '../api/user';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPaperPlane, faReply } from '@fortawesome/free-solid-svg-icons';

const PAGE_SIZE = 20;

const formatDate = dateStr => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', ...(sameYear ? {} : { year: '2-digit' }),
    });
};

const MD_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;
const SAFE_URL_REGEX = /^https?:\/\//i;

const renderBody = (text) => {
    const parts = [];
    MD_LINK_REGEX.lastIndex = 0;
    let lastIndex = 0;
    let match;

    while ((match = MD_LINK_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        const href = match[2];
        if (SAFE_URL_REGEX.test(href) || href.startsWith('/')) {
            parts.push(
                <a key={match.index} href={href} target="_blank" rel="noopener noreferrer">
                    {match[1]}
                </a>
            );
        } else {
            parts.push(match[1]);
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts;
};

const ComposeForm = ({ currentUser, onSent, replyTo }) => {
    const replySubject = replyTo
        ? (replyTo.subject.startsWith('Re: ') ? replyTo.subject : `Re: ${replyTo.subject}`)
        : '';
    const replyBody = replyTo
        ? `\n\n--- Original message from ${replyTo.senderUsername} ---\n${replyTo.body}`
        : '';

    const [subject, setSubject] = useState(replySubject);
    const [body, setBody] = useState(replyBody ? '' : '');
    const [quotedBody] = useState(replyBody);
    const [recipientUserID, setRecipientUserID] = useState(
        replyTo ? String(replyTo.senderUserID || '') : ''
    );
    const [isBroadcast, setIsBroadcast] = useState(false);
    const [sending, setSending] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);

    const isAdmin = currentUser?.isAdmin;

    useEffect(() => {
        if (!isAdmin || usersLoaded) return;
        let cancelled = false;
        fetchUsers().then(data => {
            if (cancelled) return;
            if (data?.users) {
                const sorted = data.users
                    .filter(u => !u.isAdmin)
                    .sort((a, b) => {
                        const aTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
                        const bTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
                        return bTime - aTime;
                    });
                setUsers(sorted);
            }
            setUsersLoaded(true);
        }).catch(() => {
            if (!cancelled) setUsersLoaded(true);
        });
        return () => { cancelled = true; };
    }, [isAdmin, usersLoaded]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!subject.trim() || !body.trim()) {
            toast.error('Subject and body are required');
            return;
        }
        if (isAdmin && !isBroadcast && !recipientUserID) {
            toast.error('Select a recipient or broadcast');
            return;
        }

        setSending(true);
        const fullBody = body + quotedBody;
        try {
            if (isAdmin && isBroadcast) {
                const result = await broadcastMessage({ subject, body: fullBody });
                toast.success(`Broadcast sent to ${result.count} users`);
            } else if (isAdmin || replyTo) {
                await sendMessage({ recipientUserID, subject, body: fullBody });
                toast.success('Message sent');
            } else {
                await sendMessage({ subject, body: fullBody });
                toast.success('Message sent to admin');
            }
            setSubject('');
            setBody('');
            setRecipientUserID('');
            setIsBroadcast(false);
            onSent();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    return (
        <form className="message-compose" onSubmit={handleSubmit}>
            <h3>{replyTo ? `Reply to ${replyTo.senderUsername}` : 'New Message'}</h3>
            {isAdmin && !replyTo && (
                <div className="compose-recipient">
                    <label className="broadcast-toggle">
                        <input
                            type="checkbox"
                            checked={isBroadcast}
                            onChange={e => setIsBroadcast(e.target.checked)}
                        />
                        Broadcast to all users
                    </label>
                    {!isBroadcast && (
                        <select
                            value={recipientUserID}
                            onChange={e => setRecipientUserID(e.target.value)}
                        >
                            <option value="">Select recipient...</option>
                            {users.map(u => (
                                <option key={u.userID} value={u.userID}>{u.username}</option>
                            ))}
                        </select>
                    )}
                </div>
            )}
            <input
                type="text"
                placeholder="Subject"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                maxLength={255}
            />
            <textarea
                placeholder="Message body..."
                value={body}
                onChange={e => setBody(e.target.value)}
                maxLength={5000}
                rows={4}
            />
            {quotedBody && (
                <div className="compose-quoted">
                    <span className="quoted-label">Original message</span>
                    <p>{replyTo.body}</p>
                </div>
            )}
            <button type="submit" className="btn-send" disabled={sending}>
                <FontAwesomeIcon icon={faPaperPlane} />
                {sending ? 'Sending...' : 'Send'}
            </button>
        </form>
    );
};

ComposeForm.propTypes = {
    currentUser: PropTypes.shape({ isAdmin: PropTypes.bool }),
    onSent: PropTypes.func.isRequired,
    replyTo: PropTypes.shape({
        senderUserID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        senderUsername: PropTypes.string,
        subject: PropTypes.string,
        body: PropTypes.string,
    }),
};

const messagePropType = PropTypes.shape({
    messageID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    senderUserID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    senderUsername: PropTypes.string,
    recipientUsername: PropTypes.string,
    subject: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    readAt: PropTypes.string,
    created: PropTypes.string.isRequired,
});

const MessageRow = React.memo(({ message, onRead, onReply }) => {
    const [expanded, setExpanded] = useState(false);
    const isUnread = !message.readAt;

    const handleClick = async () => {
        setExpanded(!expanded);
        if (isUnread && !expanded) {
            try {
                await markMessageAsRead(message.messageID);
                onRead(message.messageID);
            } catch (err) {
                console.error('Failed to mark message as read:', err);
            }
        }
    };

    return (
        <div className={`message-row ${isUnread ? 'unread' : ''} ${expanded ? 'expanded' : ''}`} onClick={handleClick}>
            <div className="message-row-header">
                {isUnread && <span className="unread-dot" />}
                <span className="message-from">{message.senderUsername || 'Unknown'}</span>
                <span className="message-subject">{message.subject}</span>
                <span className="message-date">{formatDate(message.created)}</span>
            </div>
            {expanded && (
                <div className="message-body">
                    {renderBody(message.body)}
                    <button
                        className="btn-reply"
                        onClick={(e) => { e.stopPropagation(); onReply(message); }}
                    >
                        <FontAwesomeIcon icon={faReply} />
                        Reply
                    </button>
                </div>
            )}
        </div>
    );
});

MessageRow.displayName = 'MessageRow';
MessageRow.propTypes = {
    message: messagePropType.isRequired,
    onRead: PropTypes.func.isRequired,
    onReply: PropTypes.func.isRequired,
};

const SentRow = React.memo(({ message }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className={`message-row sent ${expanded ? 'expanded' : ''}`} onClick={() => setExpanded(!expanded)}>
            <div className="message-row-header">
                <span className="message-to">To: {message.recipientUsername || 'Unknown'}</span>
                <span className="message-subject">{message.subject}</span>
                <span className="message-date">{formatDate(message.created)}</span>
            </div>
            {expanded && (
                <div className="message-body">
                    {renderBody(message.body)}
                </div>
            )}
        </div>
    );
});

SentRow.displayName = 'SentRow';
SentRow.propTypes = {
    message: messagePropType.isRequired,
};

const MessagesPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const [, setUnreadCount] = useAtom(unreadMessageCountAtom);
    const [tab, setTab] = useState('inbox');
    const [showCompose, setShowCompose] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [messages, setMessages] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);

    const loadMessages = useCallback(async (currentTab, currentOffset) => {
        setLoading(true);
        try {
            const data = currentTab === 'inbox'
                ? await fetchInbox(PAGE_SIZE, currentOffset)
                : await fetchSent(PAGE_SIZE, currentOffset);
            setMessages(data.messages);
            setTotal(data.total);
            if (currentTab === 'inbox' && data.unreadCount !== undefined) {
                setUnreadCount(data.unreadCount);
            }
        } catch (err) {
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [setUnreadCount]);

    useEffect(() => {
        loadMessages(tab, offset);
    }, [tab, offset, loadMessages]);

    const handleTabChange = (newTab) => {
        setTab(newTab);
        setOffset(0);
        setShowCompose(false);
        setReplyTo(null);
    };

    const handleReply = useCallback((message) => {
        setReplyTo(message);
        setShowCompose(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleRead = useCallback(async (messageID) => {
        try {
            const count = await fetchUnreadCount();
            setUnreadCount(count);
        } catch (err) {
            console.error('Failed to fetch unread count:', err);
        }
        setMessages(prev =>
            prev.map(m =>
                String(m.messageID) === String(messageID)
                    ? { ...m, readAt: m.readAt || new Date().toISOString() }
                    : m
            )
        );
    }, [setUnreadCount]);

    const handleSent = () => {
        setShowCompose(false);
        setReplyTo(null);
        loadMessages(tab, offset);
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div className="messages-page">
            <div className="messages-header">
                <h2>Messages</h2>
                {showCompose ? (
                    <button
                        className="btn-cancel"
                        onClick={() => { setShowCompose(false); setReplyTo(null); }}
                    >
                        Cancel
                    </button>
                ) : (
                    <button
                        className="btn-new-message"
                        onClick={() => { setReplyTo(null); setShowCompose(true); }}
                    >
                        <FontAwesomeIcon icon={faEnvelope} />
                        New Message
                    </button>
                )}
            </div>

            {showCompose && (
                <ComposeForm key={replyTo?.messageID || 'new'} currentUser={currentUser} onSent={handleSent} replyTo={replyTo} />
            )}

            <div className="messages-tabs">
                <button
                    className={`tab ${tab === 'inbox' ? 'active' : ''}`}
                    onClick={() => handleTabChange('inbox')}
                >
                    Inbox
                </button>
                <button
                    className={`tab ${tab === 'sent' ? 'active' : ''}`}
                    onClick={() => handleTabChange('sent')}
                >
                    Sent
                </button>
            </div>

            {loading ? (
                <LoadingSpinner />
            ) : messages.length === 0 ? (
                <div className="messages-empty">
                    <FontAwesomeIcon icon={faEnvelope} className="empty-icon" />
                    <p>No messages yet</p>
                    {!showCompose && (
                        <button className="btn-new-message" onClick={() => setShowCompose(true)}>
                            <FontAwesomeIcon icon={faEnvelope} />
                            Send a Message
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="messages-list">
                        {tab === 'inbox'
                            ? messages.map(m => (
                                <MessageRow key={m.messageID} message={m} onRead={handleRead} onReply={handleReply} />
                            ))
                            : messages.map(m => (
                                <SentRow key={m.messageID} message={m} />
                            ))
                        }
                    </div>

                    {totalPages > 1 && (
                        <div className="messages-pagination">
                            <button
                                disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button
                                disabled={currentPage >= totalPages}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MessagesPage;
